import { ReactNode, useMemo, useSyncExternalStore } from 'react';
import { authService } from '@/services/auth/authService';

export interface PermissionGateProps {
  /** Required permission(s). Dot-notation: "Module.Entity.Action" */
  permission: string | string[];
  /** Match mode: 'all' requires all permissions, 'any' requires at least one */
  mode?: 'all' | 'any';
  /** Content to render when user has required permission(s) */
  children: ReactNode;
  /** Optional fallback when permission is denied (defaults to null) */
  fallback?: ReactNode;
}

/**
 * Conditionally renders children based on user permissions fetched from ABP.
 *
 * This is a UX convenience — the backend is authoritative for security.
 * PermissionGate hides UI elements the user can't interact with.
 *
 * @example
 * <PermissionGate permission="Sales.Dashboard.View">
 *   <SalesOverviewDashboard />
 * </PermissionGate>
 *
 * @example
 * <PermissionGate permission={["Sales.Order.Create", "Sales.Order.Update"]} mode="any">
 *   <OrderForm />
 * </PermissionGate>
 */
export function PermissionGate({
  permission,
  mode = 'all',
  children,
  fallback = null,
}: PermissionGateProps) {
  const hasPermission = useHasPermission(permission, mode);
  return hasPermission ? <>{children}</> : <>{fallback}</>;
}

const subscribe = (cb: () => void) => authService.subscribePermissions(cb);
const getSnapshot = () => authService.getPermissions();

/**
 * The current user's granted permissions. Reacts to permission changes.
 *
 * Prefer {@link useHasPermission} for a single check. Use this when a list has to be
 * filtered by permission — one subscription for the whole list, instead of a hook per
 * item, which the rules of hooks would not allow for a variable-length list.
 */
export function usePermissions(): string[] {
  return useSyncExternalStore(subscribe, getSnapshot);
}

/**
 * Hook to check if the current user has the given permission(s).
 * Reacts to permission changes (e.g. after token refresh picks up new grants).
 */
export function useHasPermission(
  permission: string | string[],
  mode: 'all' | 'any' = 'all',
): boolean {
  const permissions = useSyncExternalStore(subscribe, getSnapshot);

  return useMemo(() => {
    const required = Array.isArray(permission) ? permission : [permission];
    if (required.length === 0) return true;

    return mode === 'all'
      ? required.every((p) => permissions.includes(p))
      : required.some((p) => permissions.includes(p));
  }, [permissions, permission, mode]);
}
